import { supabase } from '@/integrations/supabase/client';
import {
  CheckIn,
  CheckInData,
  CheckInStatus,
  CheckInResult,
  CheckInFilters,
  UserCheckInHistory,
  DisplayCheckInStats,
  LocationData,
} from '../types';

export class CheckInService {
  /**
   * Check in to a display
   */
  static async checkIn(
    displayId: number,
    data?: CheckInData,
    location?: LocationData
  ): Promise<CheckInResult> {
    try {
      const { data: result, error } = await supabase.rpc('handle_display_checkin', {
        p_display_id: displayId,
        p_note: data?.note || null,
        p_rating: data?.rating || null,
        p_latitude: location?.latitude || null,
        p_longitude: location?.longitude || null,
        p_photo_url: data?.photo_url || null,
      });

      if (error) {
        console.error('Check-in error:', error);
        return {
          success: false,
          error: error.message,
        };
      }

      return {
        success: true,
        ...result,
      };
    } catch (error) {
      console.error('Check-in service error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * Get check-in status for a display
   */
  static async getCheckInStatus(displayId: number): Promise<CheckInStatus> {
    try {
      const { data, error } = await supabase.rpc('get_display_checkin_status', {
        p_display_id: displayId,
      });

      if (error) {
        console.error('Error fetching check-in status:', error);
        throw error;
      }

      return data as CheckInStatus;
    } catch (error) {
      console.error('Check-in status service error:', error);
      throw error;
    }
  }

  /**
   * Get batch check-in statuses for multiple displays
   */
  static async getBatchCheckInStatuses(
    displayIds: number[]
  ): Promise<Record<number, CheckInStatus>> {
    try {
      const promises = displayIds.map(async (id) => {
        const status = await this.getCheckInStatus(id);
        return [id, status] as const;
      });

      const results = await Promise.all(promises);
      return Object.fromEntries(results);
    } catch (error) {
      console.error('Batch check-in status service error:', error);
      return {};
    }
  }

  /**
   * Get user's check-in history
   */
  static async getUserCheckInHistory(
    userId?: string,
    filters?: CheckInFilters,
    limit = 50,
    offset = 0
  ): Promise<UserCheckInHistory> {
    try {
      let query = supabase
        .from('display_checkins')
        .select(`
          *,
          displays:display_id (
            id,
            name,
            location,
            latitude,
            longitude
          )
        `)
        .order('checked_in_at', { ascending: false })
        .range(offset, offset + limit - 1);

      if (userId) {
        query = query.eq('user_id', userId);
      }

      if (filters?.date_range) {
        query = query
          .gte('checked_in_at', filters.date_range.start)
          .lte('checked_in_at', filters.date_range.end);
      }

      if (filters?.display_id) {
        query = query.eq('display_id', filters.display_id);
      }

      if (filters?.location_verified !== undefined) {
        query = query.eq('location_verified', filters.location_verified);
      }

      if (filters?.has_rating) {
        query = query.not('rating', 'is', null);
      }

      if (filters?.has_photo) {
        query = query.not('photo_url', 'is', null);
      }

      if (filters?.is_public !== undefined) {
        query = query.eq('is_public', filters.is_public);
      }

      const { data: checkins, error } = await query;

      if (error) throw error;

      // Get total count and unique displays
      const { count: totalCount } = await supabase
        .from('display_checkins')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId || 'current_user');

      const { count: uniqueDisplays } = await supabase
        .from('display_checkins')
        .select('display_id', { count: 'exact', head: true })
        .eq('user_id', userId || 'current_user');

      // Get user achievements
      const { data: achievements } = await supabase
        .from('user_checkin_achievements')
        .select('*')
        .eq('user_id', userId || 'current_user')
        .order('earned_at', { ascending: false });

      return {
        checkins: checkins || [],
        total_count: totalCount || 0,
        unique_displays: uniqueDisplays || 0,
        achievements: achievements || [],
      };
    } catch (error) {
      console.error('Error fetching user check-in history:', error);
      throw error;
    }
  }

  /**
   * Get check-in statistics for a display
   */
  static async getDisplayCheckInStats(displayId: number): Promise<DisplayCheckInStats> {
    try {
      // Get basic stats from display
      const { data: display, error: displayError } = await supabase
        .from('displays')
        .select('checkin_count, unique_visitors_count, last_checkin_at')
        .eq('id', displayId)
        .single();

      if (displayError) throw displayError;

      // Get recent check-ins
      const { data: recentCheckins, error: checkinsError } = await supabase
        .from('display_checkins')
        .select(`
          *,
          user_profiles:user_id (
            full_name,
            display_name,
            username,
            avatar_url
          )
        `)
        .eq('display_id', displayId)
        .eq('is_public', true)
        .order('checked_in_at', { ascending: false })
        .limit(10);

      if (checkinsError) throw checkinsError;

      // Calculate average rating
      const { data: ratings } = await supabase
        .from('display_checkins')
        .select('rating')
        .eq('display_id', displayId)
        .not('rating', 'is', null);

      const averageRating = ratings && ratings.length > 0
        ? ratings.reduce((sum, r) => sum + (r.rating || 0), 0) / ratings.length
        : undefined;

      // Calculate location verification percentage
      const { count: totalWithLocation } = await supabase
        .from('display_checkins')
        .select('*', { count: 'exact', head: true })
        .eq('display_id', displayId)
        .not('latitude', 'is', null);

      const { count: verifiedLocation } = await supabase
        .from('display_checkins')
        .select('*', { count: 'exact', head: true })
        .eq('display_id', displayId)
        .eq('location_verified', true);

      const locationVerifiedPercentage = totalWithLocation && totalWithLocation > 0
        ? ((verifiedLocation || 0) / totalWithLocation) * 100
        : undefined;

      return {
        total_checkins: display?.checkin_count || 0,
        unique_visitors: display?.unique_visitors_count || 0,
        last_checkin_at: display?.last_checkin_at,
        recent_checkins: recentCheckins || [],
        average_rating: averageRating,
        location_verified_percentage: locationVerifiedPercentage,
      };
    } catch (error) {
      console.error('Error fetching display check-in stats:', error);
      throw error;
    }
  }

  /**
   * Update a check-in
   */
  static async updateCheckIn(
    checkInId: string,
    data: Partial<CheckInData>
  ): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('display_checkins')
        .update({
          note: data.note,
          rating: data.rating,
          photo_url: data.photo_url,
          is_public: data.is_public,
        })
        .eq('id', checkInId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error updating check-in:', error);
      return false;
    }
  }

  /**
   * Delete a check-in
   */
  static async deleteCheckIn(checkInId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('display_checkins')
        .delete()
        .eq('id', checkInId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error deleting check-in:', error);
      return false;
    }
  }

  /**
   * Get current location
   */
  static async getCurrentLocation(): Promise<LocationData | null> {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve(null);
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
          });
        },
        (error) => {
          console.warn('Error getting location:', error);
          resolve(null);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000, // 5 minutes
        }
      );
    });
  }
}